import React, { useState } from 'react';
import './ContactPage.scss';
import Reveal from '../Reveal/Reveal';

const TOPICS = ['General', 'Recruitment', 'Membership', 'Alumni', 'Other'];
const CONTACT_EMAIL = 'thetatauslo@gmail.com';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields) {
    const errors = {};
    if (!fields.name.trim()) {
        errors.name = 'Enter your name.';
    }
    if (!fields.email.trim()) {
        errors.email = 'Enter an email address.';
    } else if (!EMAIL_PATTERN.test(fields.email.trim())) {
        errors.email = 'Enter a valid email address.';
    }
    if (!fields.message.trim()) {
        errors.message = 'Enter a message.';
    }
    return errors;
}

// The site is a static client with no server, so it can't deliver mail
// itself. A mailto link hands the message to the one thing on every visitor's
// machine that reliably can: their own email app, pre-filled and ready to go.
function buildMailto(fields) {
    const subject = `${fields.topic}: message from ${fields.name}`;
    const body = `${fields.message}\n\nFrom: ${fields.name} (${fields.email})`;
    const params = new URLSearchParams({ subject, body });
    return `mailto:${CONTACT_EMAIL}?${params.toString()}`;
}

function ContactPage() {
    const [fields, setFields] = useState({ name: '', email: '', topic: TOPICS[0], message: '' });
    const [errors, setErrors] = useState({});
    const [sent, setSent] = useState(false);

    function handleChange(field) {
        return (event) => {
            const { value } = event.target;
            setFields((prev) => ({ ...prev, [field]: value }));
        };
    }

    function handleSubmit(event) {
        event.preventDefault();
        const nextErrors = validate(fields);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            return;
        }
        window.location.href = buildMailto(fields);
        setSent(true);
    }

    return (
        <div className="ContactPage">
            <header className="contact-hero">
                <Reveal as="h1" variant="mask" className="contact-hero-title">Contact Theta Tau</Reveal>
                <Reveal as="p" variant="rise" index={1} className="contact-hero-subtitle">
                    Questions about recruitment, membership, or anything else? Send us a message.
                </Reveal>
            </header>

            <Reveal as="section" variant="rise" index={2} className="contact-form-section" aria-labelledby="contact-form-heading">
                <h2 id="contact-form-heading" className="visually-hidden">Contact form</h2>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="contact-field">
                        <label htmlFor="contact-name">Name</label>
                        <input
                            id="contact-name"
                            name="name"
                            type="text"
                            autoComplete="name"
                            required
                            value={fields.name}
                            onChange={handleChange('name')}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        />
                        {errors.name && (
                            <p id="contact-name-error" className="contact-field-error">{errors.name}</p>
                        )}
                    </div>

                    <div className="contact-field">
                        <label htmlFor="contact-email">Email</label>
                        <input
                            id="contact-email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={fields.email}
                            onChange={handleChange('email')}
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        />
                        {errors.email && (
                            <p id="contact-email-error" className="contact-field-error">{errors.email}</p>
                        )}
                    </div>

                    <div className="contact-field">
                        <label htmlFor="contact-topic">Topic</label>
                        <select
                            id="contact-topic"
                            name="topic"
                            value={fields.topic}
                            onChange={handleChange('topic')}
                        >
                            {TOPICS.map((topic) => (
                                <option key={topic} value={topic}>{topic}</option>
                            ))}
                        </select>
                    </div>

                    <div className="contact-field">
                        <label htmlFor="contact-message">Message</label>
                        <textarea
                            id="contact-message"
                            name="message"
                            rows={6}
                            required
                            value={fields.message}
                            onChange={handleChange('message')}
                            aria-invalid={Boolean(errors.message)}
                            aria-describedby={errors.message ? 'contact-message-error' : undefined}
                        />
                        {errors.message && (
                            <p id="contact-message-error" className="contact-field-error">{errors.message}</p>
                        )}
                    </div>

                    <div className="contact-form-actions">
                        <button type="submit" className="btn btn--filled">
                            Send Message
                        </button>
                    </div>

                    <p className="contact-form-feedback" role="status" aria-live="polite">
                        {sent ? 'Your email app should now be open with this message ready to send.' : ''}
                    </p>
                </form>
            </Reveal>
        </div>
    );
}

export default ContactPage;
